/**
 * Workspace changes against a baseline revision.
 *
 * Workspaces are file maps in a Durable Object, not git checkouts, so there is
 * no working tree to ask for status. The nearest real baseline is the revision
 * history (`/api/history/workspaces/{id}/revisions`), which makes this
 * "changes since a revision" rather than "git changes" — named accordingly so
 * the surface does not imply a VCS that is not there.
 */

import { Schema } from "effect";

export const WorkspaceChangeKind = Schema.Literals(["added", "modified", "deleted"]);
export type WorkspaceChangeKind = typeof WorkspaceChangeKind.Type;
export const WorkspaceChange = Schema.Struct({ path: Schema.String, kind: WorkspaceChangeKind, added: Schema.Number, removed: Schema.Number });
export type WorkspaceChange = typeof WorkspaceChange.Type;
export const WorkspaceChangeSummary = Schema.Struct({ changes: Schema.Array(WorkspaceChange), added: Schema.Number, removed: Schema.Number });
export type WorkspaceChangeSummary = typeof WorkspaceChangeSummary.Type;

function lines(source: string): string[] {
  if (source === "") return [];
  const split = source.split("\n");
  // A trailing newline terminates the last line rather than starting a new one.
  if (split.length > 1 && split[split.length - 1] === "") split.pop();
  return split;
}

/**
 * Line counts for a single file pair. Uses a longest-common-subsequence length
 * so that unchanged lines are not counted as both added and removed, which is
 * what a naive length comparison does to any edit that shifts a file.
 */
function countLineDelta(
  before: string,
  after: string
): { added: number; removed: number } {
  const left = lines(before);
  const right = lines(after);
  if (left.length === 0) return { added: right.length, removed: 0 };
  if (right.length === 0) return { added: 0, removed: left.length };

  // Rolling LCS: only the previous row is needed, keeping this O(n) in memory
  // so a large file cannot exhaust the tab.
  let previous = new Array<number>(right.length + 1).fill(0);
  let current = new Array<number>(right.length + 1).fill(0);
  for (let i = 1; i <= left.length; i += 1) {
    for (let j = 1; j <= right.length; j += 1) {
      current[j] =
        left[i - 1] === right[j - 1]
          ? previous[j - 1] + 1
          : Math.max(previous[j], current[j - 1]);
    }
    const swap = previous;
    previous = current;
    current = swap;
    current.fill(0);
  }
  const common = previous[right.length];
  return { added: right.length - common, removed: left.length - common };
}

/** Compare a baseline file map with the current one. Paths sort ascending. */
export function diffWorkspaceFiles(
  baseline: Readonly<Record<string, string>>,
  current: Readonly<Record<string, string>>
): WorkspaceChangeSummary {
  const changes: WorkspaceChange[] = [];
  const paths = new Set([...Object.keys(baseline), ...Object.keys(current)]);
  for (const path of [...paths].sort()) {
    const before = Object.hasOwn(baseline, path) ? baseline[path] : undefined;
    const after = Object.hasOwn(current, path) ? current[path] : undefined;
    if (before === undefined) {
      if (after === undefined) continue;
      changes.push({
        path,
        kind: "added",
        ...countLineDelta("", after)
      });
      continue;
    }
    if (after === undefined) {
      changes.push({ path, kind: "deleted", ...countLineDelta(before, "") });
      continue;
    }
    if (before === after) continue;
    changes.push({ path, kind: "modified", ...countLineDelta(before, after) });
  }
  return {
    changes,
    added: changes.reduce((total, change) => total + change.added, 0),
    removed: changes.reduce((total, change) => total + change.removed, 0)
  };
}

/** Case-insensitive substring filter over paths. */
export function filterChanges(
  changes: readonly WorkspaceChange[],
  term: string
): readonly WorkspaceChange[] {
  const needle = term.trim().toLowerCase();
  if (!needle) return changes;
  return changes.filter((change) =>
    change.path.toLowerCase().includes(needle)
  );
}

export interface ChangeTreeNode {
  readonly name: string;
  readonly path: string;
  readonly children: ChangeTreeNode[];
  readonly change?: WorkspaceChange;
}

/** Group changed paths into a directory tree for rendering. */
export function buildChangeTree(
  changes: readonly WorkspaceChange[]
): ChangeTreeNode[] {
  const roots: ChangeTreeNode[] = [];
  for (const change of changes) {
    const segments = change.path.split("/").filter(Boolean);
    let level = roots;
    let prefix = "";
    segments.forEach((segment, index) => {
      prefix = prefix ? `${prefix}/${segment}` : segment;
      const leaf = index === segments.length - 1;
      let node = level.find((candidate) => candidate.name === segment);
      if (!node) {
        node = {
          name: segment,
          path: prefix,
          children: [],
          ...(leaf ? { change } : {})
        };
        level.push(node);
      }
      level = node.children;
    });
  }
  return roots;
}
